import {
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Spacer,
  StackProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import { RiCloseLine } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import {
  FILTER_DELIMITER,
  FilterTypes,
  SearchAndFilterTypes,
} from "src/constants";
import * as GQL from "src/types/graphql";
import { RemoteData } from "src/types/remoteData";
import { CommonFilterProps, FormFilterController } from "./CommonFilters";
import {
  ApplyingSchoolFilter,
  AttendingSchoolFilter,
  CapacityFilter,
  ErrorFilter,
  GradeFilter,
  HistoricalStatusFilter,
  IneligibleSchoolChoiceFilter,
  PreferredLanguageFilter,
  RankFilter,
  StatusFilter,
  SubStatusFilter,
  TagFilter,
  VerificationsFilter,
  VisibilityFilter,
} from "./filters";
import { FilterSettings } from "./FilterSettings";
import { FormFiltersAdvancedTools } from "./FormFiltersAdvancedTools";
import { verifyIfFiltersAreApplied } from "./utils";

type FormFiltersProps = {
  formTemplateRemoteData: RemoteData<Error, GQL.FormTemplateFragment>;
  onToggleFilter: () => void;
  onClearFilters?: () => void;
  enrollmentPeriodId: uuid;
  hideHeader?: boolean;
} & StackProps;

export const FormFilters: React.FC<FormFiltersProps> = ({
  formTemplateRemoteData,
  onToggleFilter,
  onClearFilters,
  enrollmentPeriodId,
  hideHeader = false,
  ...stackProps
}) => {
  const flags = useFlags([
    "current-applying-schools",
    "filter-on-submit",
    "filter-drawer",
  ]);
  const useFilterOnSubmit = flags["filter-on-submit"].enabled;
  const filterDrawerEnabled = flags["filter-drawer"].enabled;

  const [searchParams, setSearchParams] = useSearchParams();
  const [isDirty, setIsDirty] = useState(false);
  const filterSettings = useMemo(
    () =>
      new UrlParamsFilterSettings(
        searchParams,
        useFilterOnSubmit
          ? () => setIsDirty(true)
          : () => setSearchParams(searchParams)
      ),
    [useFilterOnSubmit, searchParams, setSearchParams]
  );

  const formTemplateId = formTemplateRemoteData.toNullable()?.id ?? "";

  const rankingEnabled = useMemo(() => {
    return formTemplateRemoteData
      .map((data) => {
        const schoolRankingSection = data.sections.find(
          (section) =>
            section?.type ===
            GQL.form_template_section_type_enum.SchoolRankingSection
        );
        return (
          schoolRankingSection?.schools_ranking_section?.ranking_enabled ??
          false
        );
      })
      .withDefault(false);
  }, [formTemplateRemoteData]);

  const handleSubmitFilters = useCallback(() => {
    setSearchParams(searchParams);
    setIsDirty(false);
    filterDrawerEnabled && onToggleFilter();
  }, [searchParams, setSearchParams, filterDrawerEnabled, onToggleFilter]);

  const handleClearAll = useCallback(() => {
    onClearFilters && onClearFilters();
    setIsDirty(false);
    filterDrawerEnabled && onToggleFilter();
  }, [onClearFilters, filterDrawerEnabled, onToggleFilter]);

  const commonFilterProps: CommonFilterProps = { filterSettings };

  return (
    <VStack
      paddingX={4}
      spacing={4}
      align="left"
      height="100%"
      overflowY="auto"
      position="sticky"
      top={0}
      {...stackProps}
    >
      {!hideHeader && (
        <HStack>
          <Text variant="filterHeader">Filter</Text>
          <Spacer />
          <IconButton
            aria-label="close-filter"
            variant="ghost"
            icon={<RiCloseLine />}
            onClick={onToggleFilter}
          />
        </HStack>
      )}
      <FormFilterController filter={FilterTypes.School}>
        <ApplyingSchoolFilter
          formTemplateId={formTemplateId}
          {...commonFilterProps}
        />
      </FormFilterController>

      {flags["current-applying-schools"].enabled && (
        <FormFilterController filter={FilterTypes.AttendingSchool}>
          <AttendingSchoolFilter
            formTemplateId={formTemplateId}
            {...commonFilterProps}
          />
        </FormFilterController>
      )}

      <FormFilterController filter={FilterTypes.Status}>
        <StatusFilter {...commonFilterProps} />
      </FormFilterController>

      <FormFilterController filter={FilterTypes.SubStatus}>
        <SubStatusFilter {...commonFilterProps} />
      </FormFilterController>

      <FormFilterController filter={FilterTypes.SubmittedBefore}>
        <HistoricalStatusFilter {...commonFilterProps} />
      </FormFilterController>

      <FormFilterController filter={FilterTypes.Grades}>
        <GradeFilter {...commonFilterProps} />
      </FormFilterController>

      {rankingEnabled && (
        <FormFilterController filter={FilterTypes.Rank}>
          <RankFilter formTemplateId={formTemplateId} {...commonFilterProps} />
        </FormFilterController>
      )}

      <FormFilterController filter={FilterTypes.Tags}>
        <TagFilter
          enrollmentPeriodId={enrollmentPeriodId}
          formTemplateId={formTemplateId}
          {...commonFilterProps}
        />
      </FormFilterController>

      <FormFilterController filter={FilterTypes.WithinCapacity}>
        <CapacityFilter {...commonFilterProps} />
      </FormFilterController>

      <FormFilterController filter={FilterTypes.PreferredLanguage}>
        <PreferredLanguageFilter {...commonFilterProps} />
      </FormFilterController>

      <FormFilterController filter={FilterTypes.HasErrors}>
        <ErrorFilter {...commonFilterProps} />
      </FormFilterController>

      <FormFilterController filter={FilterTypes.IneligibleSchoolChoice}>
        <IneligibleSchoolChoiceFilter {...commonFilterProps} />
      </FormFilterController>

      <FormFilterController filter={FilterTypes.VerificationStatus}>
        <VerificationsFilter
          formTemplateId={formTemplateId}
          {...commonFilterProps}
        />
      </FormFilterController>

      <FormFilterController filter={FilterTypes.Visibility}>
        <VisibilityFilter {...commonFilterProps} />
      </FormFilterController>

      <Divider />

      <FormFilterController filter={FilterTypes.DynamicQuestions}>
        <FormFiltersAdvancedTools
          formTemplateRemoteData={formTemplateRemoteData}
          {...commonFilterProps}
        />
      </FormFilterController>

      {(useFilterOnSubmit || verifyIfFiltersAreApplied(searchParams)) && (
        <VStack position="sticky" background="white" align="end" bottom="0">
          <Divider />
          <Flex gap={2} width="100%">
            <Button
              onClick={handleClearAll}
              variant="outline"
              colorScheme="gray"
              size="sm"
              flex="1"
              isDisabled={!verifyIfFiltersAreApplied(searchParams) && !isDirty}
            >
              Clear all
            </Button>
            {useFilterOnSubmit && (
              <Button
                onClick={handleSubmitFilters}
                variant="solid"
                colorScheme="primary"
                size="sm"
                flex="1"
                isDisabled={!isDirty}
              >
                Filter
              </Button>
            )}
          </Flex>
        </VStack>
      )}
    </VStack>
  );
};

class UrlParamsFilterSettings extends FilterSettings<SearchAndFilterTypes> {
  constructor(
    private searchParams: URLSearchParams,
    private onChange?: (key: SearchAndFilterTypes, value: string[]) => void
  ) {
    super();
  }
  get(key: SearchAndFilterTypes): string[] {
    return this.searchParams.get(key)?.split(FILTER_DELIMITER) ?? [];
  }
  set(key: SearchAndFilterTypes, ...values: string[]) {
    if (values.length) {
      this.searchParams.set(key, values.join(FILTER_DELIMITER));
    } else {
      this.searchParams.delete(key);
    }
    this.onChange?.(key, values);
  }
}
